// src/pages/OutcomeCapturePage.jsx
//
// Buyer lands here from the D+30 email's magic-link. No password — the
// URL itself authenticates them via the HMAC token in the path. Pre-filled
// with their actual blueprint's tools so the form feels like a focused
// conversation, not a survey.
//
// Why this page is the most important page in the product:
//   Every outcome captured here makes the next buyer's report sharper.
//   After ~100 outcomes per (industry, tool) cohort, tool.backedBy.* gets
//   rebuilt from real measured outcomes — and we can write
//   "247 dental practices, median 23% no-show reduction" instead of
//   synthetic "industry typical" copy. That's the proprietary asset that
//   compounds: no LLM prompt can fabricate measured outcomes.
//
// UX principles applied:
//   - Personal, not survey-y. "How did it go?" framing, founder voice.
//   - Pre-fill everything we can (tool names from their report).
//   - One vertical scroll, no multi-step form.
//   - Per-tool: status FIRST (the only field that always matters), then
//     conditionally show outcome questions only for "implemented" / "tried".
//   - Optional NPS + free-text wins/blockers at the bottom.
//   - Testimonial consent as a single checkbox, not a separate page.
//   - On submit: warm thank-you with permission ask if they had a win.

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Sparkles, Check, AlertCircle, Loader2, Heart, MessageSquareQuote } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const API_BASE =
  process.env.REACT_APP_BACKEND_URL ||
  "https://ai-workshop-llc-backend.onrender.com";

const STATUS_OPTIONS = [
  { value: "implemented",        label: "Up and running",  sub: "I'm using it",            color: "blue" },
  { value: "tried_then_stopped", label: "Tried, stopped",  sub: "Didn't pan out",          color: "amber" },
  { value: "not_yet",            label: "Not yet",         sub: "Haven't gotten to it",    color: "slate" },
  { value: "skipped",            label: "Skipped",         sub: "Decided against it",      color: "slate" },
];

// Returns Tailwind classes for the per-status pill button.
const statusButtonClass = (active, color) => {
  if (!active) return "bg-slate-950/40 border-white/10 text-slate-400 hover:border-white/25 hover:text-slate-200";
  if (color === "blue")  return "bg-blue-500/15 border-blue-500/50 text-white";
  if (color === "amber") return "bg-amber-500/15 border-amber-500/50 text-white";
  return "bg-slate-700/40 border-slate-500/50 text-white";
};

export default function OutcomeCapturePage() {
  const { reportId, token } = useParams();

  // ── Page-level state ───────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [payload, setPayload] = useState(null); // { businessName, industry, tools[], days_since_purchase, existing_submission }

  // ── Form state ─────────────────────────────────────────────────────────
  // Per-tool state map: { tool_id: { status, hours_saved_per_week, dollars_saved_per_month, implementation_difficulty, would_recommend, notes } }
  const [toolState, setToolState] = useState({});
  const [npsOverall, setNpsOverall] = useState(null);
  const [biggestWin, setBiggestWin] = useState("");
  const [biggestBlocker, setBiggestBlocker] = useState("");
  const [testimonialConsent, setTestimonialConsent] = useState(false);
  const [testimonialQuote, setTestimonialQuote] = useState("");

  // ── Initial fetch ──────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/outcomes/${reportId}/${token}`);
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          if (!cancelled) {
            setError(body.detail || `Couldn't load (${res.status}). Try the link from your email again.`);
            setLoading(false);
          }
          return;
        }
        const data = await res.json();
        if (cancelled) return;
        setPayload(data);
        // Hydrate tool state from any existing submission so a returning
        // buyer sees what they reported last time and can update.
        const existing = data.existing_submission;
        if (existing) {
          const map = {};
          for (const o of (existing.tool_outcomes || [])) {
            map[o.tool_id] = {
              status: o.status || "",
              hours_saved_per_week: o.hours_saved_per_week ?? "",
              dollars_saved_per_month: o.dollars_saved_per_month ?? "",
              implementation_difficulty: o.implementation_difficulty ?? null,
              would_recommend: o.would_recommend ?? null,
              notes: o.notes || "",
            };
          }
          setToolState(map);
          setNpsOverall(existing.nps_overall ?? null);
          setBiggestWin(existing.biggest_win || "");
          setBiggestBlocker(existing.biggest_blocker || "");
          setTestimonialConsent(!!existing.testimonial_consent);
          setTestimonialQuote(existing.testimonial_quote || "");
        }
        setLoading(false);
      } catch (e) {
        if (!cancelled) {
          setError("Network hiccup loading your blueprint. Please refresh.");
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [reportId, token]);

  // ── Tool-state updaters ────────────────────────────────────────────────
  const setToolField = useCallback((toolId, field, value) => {
    setToolState((prev) => ({
      ...prev,
      [toolId]: { ...(prev[toolId] || {}), [field]: value },
    }));
  }, []);

  // Whether the buyer has reported anything substantive — used to gate Submit.
  const hasAnyReport = useMemo(() => {
    if (Object.values(toolState).some((s) => s?.status)) return true;
    if (npsOverall !== null) return true;
    if ((biggestWin || "").trim() || (biggestBlocker || "").trim()) return true;
    return false;
  }, [toolState, npsOverall, biggestWin, biggestBlocker]);

  // ── Submit ─────────────────────────────────────────────────────────────
  const onSubmit = async () => {
    if (!hasAnyReport) {
      toast.error("Pick a status for at least one tool, or share a quick win/blocker below.");
      return;
    }
    setSubmitting(true);
    const tool_outcomes = (payload?.tools || [])
      .map((t) => {
        const s = toolState[t.id] || {};
        if (!s.status) return null;
        return {
          tool_id: t.id,
          tool_name: t.name,
          status: s.status,
          hours_saved_per_week: s.hours_saved_per_week === "" ? null : Number(s.hours_saved_per_week) || null,
          dollars_saved_per_month: s.dollars_saved_per_month === "" ? null : Number(s.dollars_saved_per_month) || null,
          implementation_difficulty: s.implementation_difficulty || null,
          would_recommend: s.would_recommend || null,
          notes: (s.notes || "").trim() || null,
        };
      })
      .filter(Boolean);

    try {
      const res = await fetch(`${API_BASE}/api/outcomes/${reportId}/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tool_outcomes,
          nps_overall: npsOverall,
          biggest_win: biggestWin.trim() || null,
          biggest_blocker: biggestBlocker.trim() || null,
          testimonial_consent: testimonialConsent,
          testimonial_quote: (testimonialConsent ? testimonialQuote.trim() : "") || null,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Save failed (${res.status})`);
      }
      setSubmitted(true);
    } catch (e) {
      toast.error(e.message || "Couldn't save — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render: loading / error / submitted / form ────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0c1324] flex items-center justify-center px-6">
        <div className="flex items-center gap-3 text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm">Loading your blueprint…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0c1324] flex items-center justify-center px-6">
        <div className="max-w-md text-center bg-slate-900/70 border border-white/10 rounded-2xl p-8">
          <AlertCircle className="w-10 h-10 text-amber-400 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-white mb-2">Couldn't open this link</h1>
          <p className="text-sm text-slate-400 mb-6">{error}</p>
          <p className="text-xs text-slate-500">
            If the email link expired or got mangled, email{" "}
            <a href="mailto:ali.erdem@aiworkshopllc.com" className="text-blue-400 hover:underline">
              ali.erdem@aiworkshopllc.com
            </a>{" "}
            and I'll personally pull up your report.
          </p>
        </div>
      </div>
    );
  }

  if (submitted) {
    const hadWin = (biggestWin || "").trim().length > 0
      || Object.values(toolState).some((s) => Number(s?.hours_saved_per_week) > 0
                                            || Number(s?.dollars_saved_per_month) > 0);
    return (
      <div className="min-h-screen bg-[#0c1324] flex items-center justify-center px-6 py-12">
        <Helmet><title>Thanks — outcome recorded</title></Helmet>
        <div className="max-w-lg w-full bg-slate-900/70 border border-white/10 rounded-2xl p-10 text-center">
          <div className="w-14 h-14 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-5">
            <Check className="w-7 h-7 text-blue-400" />
          </div>
          <h1 className="text-2xl font-semibold text-white mb-3">Got it — thank you.</h1>
          <p className="text-sm text-slate-400 mb-6">
            Your outcomes go straight into the dataset that makes the next
            buyer's blueprint sharper. Real measured numbers replacing
            synthetic ones — that's only possible because of you.
          </p>
          {hadWin && !testimonialConsent && (
            <div className="mt-6 text-left bg-amber-500/8 border border-amber-500/20 rounded-xl p-4">
              <p className="text-xs uppercase tracking-wider text-amber-400 font-semibold mb-2 flex items-center gap-1.5">
                <Heart className="w-3.5 h-3.5" /> Mind if we feature this?
              </p>
              <p className="text-sm text-slate-300 mb-3">
                Sounds like things moved for you. We'd love to (anonymously
                if you prefer) share what you reported on our homepage.
                Want us to include your win?
              </p>
              <button
                onClick={() => { setTestimonialConsent(true); setSubmitted(false); }}
                className="text-xs font-semibold bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/30 rounded-full px-4 py-1.5"
              >
                Yes — I'll write a one-line quote
              </button>
            </div>
          )}
          <p className="mt-8 text-xs text-slate-500">
            — Ali Erdem · ali.erdem@aiworkshopllc.com
          </p>
        </div>
      </div>
    );
  }

  // Main form
  const biz = payload.businessName;
  const industryLabel = (payload.industry || "your industry").replace(/_/g, " ");
  return (
    <div className="min-h-screen bg-[#0c1324] text-white">
      <Helmet><title>How did it go? — {biz}</title></Helmet>
      <main className="max-w-2xl mx-auto px-6 py-12 sm:py-16">
        {/* Header — personal voice from Ali */}
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-blue-400 bg-blue-500/10 border border-blue-500/20 px-3 py-1 rounded-full mb-4">
            <Sparkles className="w-3.5 h-3.5" /> {payload.days_since_purchase}-day check-in
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold leading-tight mb-3">
            How did it go for {biz}?
          </h1>
          <p className="text-slate-400 text-base leading-relaxed">
            It's been about {payload.days_since_purchase} days since you got
            your AI Workshop blueprint. Tell me what worked, what didn't,
            and what's still on your list. Whatever you share goes
            straight to me — and it makes the next buyer's blueprint
            sharper, because we replace synthetic ROI numbers with what
            real {industryLabel} businesses actually measured.
          </p>
        </div>

        {/* Per-tool capture */}
        <section className="space-y-4">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400">
            Your recommended tools — what happened?
          </h2>
          {(payload.tools || []).map((tool) => {
            const s = toolState[tool.id] || {};
            const showOutcomes = s.status === "implemented" || s.status === "tried_then_stopped";
            return (
              <div
                key={tool.id}
                className="bg-slate-900/70 border border-white/10 rounded-2xl p-5"
              >
                <div className="flex items-baseline justify-between gap-3 mb-3">
                  <div>
                    <div className="text-base font-semibold text-white">{tool.name}</div>
                    {tool.category && (
                      <div className="text-xs text-slate-500 mt-0.5">{tool.category}</div>
                    )}
                  </div>
                </div>

                {/* Status chips */}
                <div className="grid grid-cols-2 gap-2">
                  {STATUS_OPTIONS.map(({ value, label, sub, color }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setToolField(tool.id, "status", s.status === value ? "" : value)}
                      className={`text-left border rounded-xl px-3 py-2.5 transition-all ${statusButtonClass(s.status === value, color)}`}
                    >
                      <div className="text-sm font-medium">{label}</div>
                      <div className="text-xs opacity-75 mt-0.5">{sub}</div>
                    </button>
                  ))}
                </div>

                {/* Outcome fields — only when implemented or tried-then-stopped */}
                {showOutcomes && (
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-slate-400">Hours saved / week</Label>
                      <input
                        type="number"
                        min="0" max="168" step="0.5"
                        inputMode="decimal"
                        placeholder="e.g. 6"
                        value={s.hours_saved_per_week ?? ""}
                        onChange={(e) => setToolField(tool.id, "hours_saved_per_week", e.target.value)}
                        className="mt-1 w-full bg-slate-950/50 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-400">$ saved / month</Label>
                      <input
                        type="number"
                        min="0" max="1000000" step="50"
                        inputMode="decimal"
                        placeholder="e.g. 1200"
                        value={s.dollars_saved_per_month ?? ""}
                        onChange={(e) => setToolField(tool.id, "dollars_saved_per_month", e.target.value)}
                        className="mt-1 w-full bg-slate-950/50 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-xs text-slate-400">
                        Setup difficulty — 1 (easy) to 5 (brutal)
                      </Label>
                      <div className="flex gap-1.5 mt-1">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <button
                            key={n}
                            type="button"
                            onClick={() => setToolField(tool.id, "implementation_difficulty", s.implementation_difficulty === n ? null : n)}
                            className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-all ${
                              s.implementation_difficulty === n
                                ? "bg-blue-500/20 border-blue-500/50 text-white"
                                : "bg-slate-950/50 border-white/10 text-slate-400 hover:border-white/20"
                            }`}
                          >
                            {n}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-xs text-slate-400">
                        Would you recommend it to another {industryLabel.toLowerCase()} business? — 1 (no) to 10 (yes, immediately)
                      </Label>
                      <div className="flex gap-1 mt-1">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                          <button
                            key={n}
                            type="button"
                            onClick={() => setToolField(tool.id, "would_recommend", s.would_recommend === n ? null : n)}
                            className={`flex-1 py-2 rounded-lg text-xs font-medium border transition-all ${
                              s.would_recommend === n
                                ? n >= 8 ? "bg-blue-500/20 border-blue-500/50 text-white"
                                : n >= 5 ? "bg-amber-500/20 border-amber-500/50 text-white"
                                : "bg-red-500/20 border-red-500/50 text-white"
                                : "bg-slate-950/50 border-white/10 text-slate-400 hover:border-white/20"
                            }`}
                          >
                            {n}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-xs text-slate-400">Anything else worth telling me about this tool? (optional)</Label>
                      <Textarea
                        rows={2}
                        value={s.notes || ""}
                        onChange={(e) => setToolField(tool.id, "notes", e.target.value)}
                        placeholder={s.status === "tried_then_stopped" ? "What got in the way?" : "What surprised you?"}
                        className="mt-1 bg-slate-950/50 border-white/10 text-white text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </section>

        {/* Overall NPS + free-text */}
        <section className="mt-10 space-y-5">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400">
            Overall — was the blueprint worth $149?
          </h2>
          <div>
            <Label className="text-xs text-slate-400">
              How likely are you to recommend AI Workshop to another business owner? — 1 to 10
            </Label>
            <div className="flex gap-1 mt-1">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setNpsOverall(npsOverall === n ? null : n)}
                  className={`flex-1 py-2 rounded-lg text-xs font-medium border transition-all ${
                    npsOverall === n
                      ? n >= 9 ? "bg-blue-500/20 border-blue-500/50 text-white"
                      : n >= 7 ? "bg-amber-500/20 border-amber-500/50 text-white"
                      : "bg-red-500/20 border-red-500/50 text-white"
                      : "bg-slate-950/50 border-white/10 text-slate-400 hover:border-white/20"
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-400">Biggest win so far? (optional)</Label>
            <Textarea
              rows={2}
              value={biggestWin}
              onChange={(e) => setBiggestWin(e.target.value)}
              placeholder="e.g. Goodcall recovered 4 missed calls in week 1 — about $800 of work I would have lost."
              className="mt-1 bg-slate-950/50 border-white/10 text-white text-sm"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Biggest blocker? (optional — and the most useful field for me)</Label>
            <Textarea
              rows={2}
              value={biggestBlocker}
              onChange={(e) => setBiggestBlocker(e.target.value)}
              placeholder="e.g. Couldn't figure out how to wire it into our calendar."
              className="mt-1 bg-slate-950/50 border-white/10 text-white text-sm"
            />
          </div>
        </section>

        {/* Testimonial consent */}
        <section className="mt-10 bg-amber-500/5 border border-amber-500/20 rounded-2xl p-5">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={testimonialConsent}
              onChange={(e) => setTestimonialConsent(e.target.checked)}
              className="mt-1 accent-amber-400 w-4 h-4 flex-shrink-0"
            />
            <div>
              <div className="text-sm font-semibold text-white flex items-center gap-1.5">
                <MessageSquareQuote className="w-4 h-4 text-amber-300" />
                Yes — you can quote me
              </div>
              <div className="text-xs text-slate-400 mt-0.5">
                We may feature your win on our homepage or landing pages.
                Use first name + business or anonymize — your call.
              </div>
            </div>
          </label>
          {testimonialConsent && (
            <Textarea
              rows={2}
              value={testimonialQuote}
              onChange={(e) => setTestimonialQuote(e.target.value)}
              placeholder="One line you'd be okay with us featuring (optional — we'll write something based on what you shared if you don't)."
              className="mt-3 bg-slate-950/50 border-white/10 text-white text-sm"
            />
          )}
        </section>

        {/* Submit */}
        <div className="mt-10 flex justify-end">
          <Button
            onClick={onSubmit}
            disabled={submitting || !hasAnyReport}
            className="bg-gradient-to-br from-blue-500 to-blue-600 text-white px-7 font-semibold rounded-full disabled:opacity-50"
          >
            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : "Send to Ali"}
          </Button>
        </div>

        <p className="mt-6 text-xs text-slate-500 text-center">
          Goes straight to ali.erdem@aiworkshopllc.com — no automated mailing list, no upsell.
        </p>
      </main>
    </div>
  );
}
